import { getSession } from '@/lib/auth-client'
import { isTrustCenterHostModePath } from '@/lib/env'
import { queryClient, trpc, trpcClient } from '@/lib/trpc'
import { SessionObject } from '@/stores/sessionStore'
import { createFileRoute, isRedirect, Outlet, redirect } from '@tanstack/react-router'
import { useEffect } from 'react'
import { clearWorkspaceContextCache, useAuthCheck } from '../../lib/auth'
import { usePresenceHeartbeat } from '@/hooks/use-presence-heartbeat'

const MFA_SETUP_PENDING_REDIRECT_KEY = 'mfa-setup:pending-redirect'

// Helper functions for organization/workspace management
function getStoredActiveWorkspace() {
  try {
    const storedWorkspace = localStorage.getItem('auth:activeWorkspace');
    return storedWorkspace ? JSON.parse(storedWorkspace) : null;
  } catch (error) {
    console.error('Error parsing stored workspace:', error);
    return null;
  }
}

function pickDefaultWorkspace(workspaces: any[] | undefined) {
  if (!workspaces?.length) return null;
  return workspaces.find((workspace) => workspace.type === 'SCREENATA') ?? workspaces[0];
}

function getStoredWorkspaceInOrganization(organization: any) {
  const storedWorkspace = getStoredActiveWorkspace();
  if (!storedWorkspace?.id || !organization?.workspaces?.length) return null;
  return organization.workspaces.find((workspace: any) => workspace.id === storedWorkspace.id) ?? null;
}

function isOrgMfaEnforcementActive(organization: any) {
  return organization?.mfaRequired === true;
}

// Extract workspace ID from URL path
function extractWorkspaceIdFromPath(path: string): string | null {
  const match = path.match(/\/workspaces\/([^\/]+)/);
  return match ? match[1] : null;
}

/**
 * True if the URL workspace id matches what we previously cached for this
 * session. We only attempt recovery in that case — a 403 on a *new* URL the
 * user just typed is genuine "not authorized," not stale state.
 */
function isStaleCachedWorkspace(urlWorkspaceId: string | null): boolean {
  if (!urlWorkspaceId) return true; // no URL workspace; no-URL recovery path applies
  const cached = localStorage.getItem('auth:activeWorkspaceId');
  return cached !== null && cached === urlWorkspaceId;
}

/**
 * Stale-ID recovery: catches the case where localStorage / cached session
 * carries workspace or organization IDs that no longer exist in the database
 * (e.g. after a one-shot ID rewrite migration, or after a workspace was
 * deleted). Clears stale caches, refetches the session fresh, picks the user's
 * first available workspace, and redirects.
 *
 * Throws a TanStack Router redirect on success. Returns false if the user has
 * no recoverable organization/workspace (caller should fall through to
 * onboarding / 403).
 */
async function recoverFromStaleIds(targetPath: string): Promise<false> {
  const urlWorkspaceId = extractWorkspaceIdFromPath(targetPath);
  if (!isStaleCachedWorkspace(urlWorkspaceId)) {
    // The URL workspace was never in our cache — this is a genuine "not
    // authorized" rather than a stale-cache situation. Don't perform an
    // expensive session refresh; let the caller redirect to /403.
    return false;
  }

  console.warn(
    '[stale-id-recovery] Cached workspace/org IDs no longer resolve. Clearing local cache and re-resolving from server.',
  );

  // Drop cached workspace/org context. Session cache also goes because it
  // carries `activeOrganizationId`, which post-rewrite points at a row that
  // no longer exists. Token/expiry stay so the user remains signed in.
  clearWorkspaceContextCache();
  queryClient.removeQueries({ queryKey: trpc.workspaceMembers.getMyRole.queryKey() });
  queryClient.removeQueries({ queryKey: trpc.organizations.getWorkspaceContext.queryKey() });

  // Refetch session straight from the server. `getSession()` re-stores
  // `auth:session` with the fresh values.
  const fresh = await getSession();
  if (!fresh?.session?.activeOrganizationId) return false;

  let workspaceId: string | undefined;
  try {
    const { organization } =
      await trpcClient.organizations.getOrganizationWithWorkspaces.query({
        id: fresh.session.activeOrganizationId,
      });
    if (organization) {
      localStorage.setItem('auth:activeOrg', JSON.stringify(organization));
      const defaultWorkspace = pickDefaultWorkspace(organization.workspaces);
      if (defaultWorkspace) {
        localStorage.setItem('auth:activeWorkspace', JSON.stringify(defaultWorkspace));
        localStorage.setItem('auth:activeWorkspaceId', defaultWorkspace.id);
        workspaceId = defaultWorkspace.id;
      }
    }
  } catch (e) {
    console.error('[stale-id-recovery] Failed to resolve fresh org/workspace:', e);
    return false;
  }

  if (!workspaceId) return false;

  const nextPath = urlWorkspaceId
    ? targetPath.replace(`/workspaces/${urlWorkspaceId}`, `/workspaces/${workspaceId}`)
    : `/workspaces/${workspaceId}`;

  console.warn(`[stale-id-recovery] Redirecting to ${nextPath}`);
  throw redirect({ to: nextPath });
}

async function ensureOrganizationAndWorkspaceData(session: SessionObject, targetPath: string) {
  // Check if URL contains a workspace ID
  const workspaceIdFromUrl = extractWorkspaceIdFromPath(targetPath);

  if (workspaceIdFromUrl) {
    // URL-based navigation - use single API call to handle everything
    try {
      // 前置校验：用户是否为该空间成员（前端拦截，避免错误写入缓存）
      const roleOptions = trpc.workspaceMembers.getMyRole.queryOptions({
        targetWorkspaceId: workspaceIdFromUrl,
      });
      const sessionScope = {
        sessionId: session.session.id,
        activeOrganizationId: session.session.activeOrganizationId,
        delegatedOrganizationId: (session.session as { delegatedOrganizationId?: string }).delegatedOrganizationId,
      };
      const myRole = await queryClient.fetchQuery({
        ...roleOptions,
        queryKey: [...roleOptions.queryKey, sessionScope],
        staleTime: 30_000,
      });

      if (!myRole?.role) {
        // The cached workspace ID may have been rewritten by a backfill (it
        // no longer exists, so getMyRole returns null). Try to recover by
        // resolving the user's first available workspace. On success the
        // helper throws a redirect; on failure we fall through to /403.
        await recoverFromStaleIds(targetPath);
        throw redirect({ to: '/403' });
      }

      // Redirect restricted roles to their dedicated centers before rendering
      if (
        myRole.role === 'EMPLOYEE' &&
        !targetPath.includes('/employee-center')
      ) {
        throw redirect({
          to: '/workspaces/$workspaceId/employee-center',
          params: { workspaceId: workspaceIdFromUrl },
        });
      }

      if (
        myRole.role === 'AUDITOR' &&
        !targetPath.includes('/auditor-center')
      ) {
        throw redirect({
          to: '/auditor-center',
        });
      }

      const contextOptions = trpc.organizations.getWorkspaceContext.queryOptions({
        workspaceId: workspaceIdFromUrl,
      });
      const contextKey = [...contextOptions.queryKey, sessionScope];
      const result = await queryClient.fetchQuery({
        ...contextOptions,
        queryKey: contextKey,
        staleTime: localStorage.getItem('auth:activeWorkspaceId') === workspaceIdFromUrl ? 30_000 : 0,
      });
      if (result.needsReload) queryClient.removeQueries({ queryKey: contextKey, exact: true });

      // Cache organization and workspace data
      if (result.organization) {
        localStorage.setItem('auth:activeOrg', JSON.stringify(result.organization));
      }
      if (result.workspace) {
        localStorage.setItem('auth:activeWorkspace', JSON.stringify(result.workspace));
        localStorage.setItem('auth:activeWorkspaceId', result.workspace.id);
      }

      // Store delegation context if delegation is active (either just set or already active)
      if (result.delegationActive) {
        // Get delegation context - either from API response or construct from workspace org
        const delegationContext = result.delegationContext || {
          clientOrgId: result.workspace.organizationId,
          clientOrgName: result.organization.name,
          partnerOrgId: result.organization.parentPartnerId
        };

        localStorage.setItem('partner:delegation', JSON.stringify({
          isDelegate: true,
          ...delegationContext,
          timestamp: Date.now()
        }));
      } else {
        // Clear delegation context if not delegating
        localStorage.removeItem('partner:delegation');
      }

      // If delegation was set or org was switched, need to reload
      if (result.needsReload) {
        return {
          organization: result.organization,
          workspace: result.workspace,
          needsReload: true
        };
      }

      return {
        organization: result.organization,
        workspace: result.workspace,
        needsReload: false
      };
    } catch (error: any) {
      // Re-throw TanStack Router redirects immediately
      if (isRedirect(error)) throw error;
      console.error('❌ Failed to load workspace context:', error);
      if (error?.data?.code === 'UNAUTHORIZED') {
        throw redirect({
          to: '/login',
          search: {
            error: 'not-authorized',
            redirect_uri: window.location.href,
          },
        })
      }
      if (error?.data?.code === 'FORBIDDEN' || error?.data?.code === 'NOT_FOUND') {
        // Try stale-ID recovery first: localStorage may carry an old ID that
        // was rewritten by a backfill, or the workspace may have been deleted.
        // Recovery throws a redirect on success; falls through here on failure.
        await recoverFromStaleIds(targetPath);

        throw redirect({
          to: '/403',
          search: { error: error.message || 'Workspace not accessible' }
        });
      }
      throw error;
    }
  }

  // No workspace in URL - fetch user's active organization
  if (!session.session.activeOrganizationId) {
    return { organization: null, workspace: null, needsReload: false };
  }

  try {
    const delegatedOrgId = (session.session as any)?.delegatedOrganizationId;
    const orgIdToFetch = delegatedOrgId || session.session.activeOrganizationId;

    const { organization: orgWithWorkspaces } = await trpcClient.organizations.getOrganizationWithWorkspaces.query({
      id: orgIdToFetch
    });

    let workspace = getStoredWorkspaceInOrganization(orgWithWorkspaces);

    if (orgWithWorkspaces) {
      localStorage.setItem('auth:activeOrg', JSON.stringify(orgWithWorkspaces));

      // Set a workspace if no valid workspace for this organization is stored.
      if (!workspace && orgWithWorkspaces.workspaces?.length > 0) {
        workspace = pickDefaultWorkspace(orgWithWorkspaces.workspaces);
        localStorage.setItem('auth:activeWorkspace', JSON.stringify(workspace));
        localStorage.setItem('auth:activeWorkspaceId', workspace.id);
      }

      return { organization: orgWithWorkspaces, workspace, needsReload: false };
    }

    return { organization: null, workspace, needsReload: false };
  } catch (error: any) {
    if (isRedirect(error)) throw error;
    console.error('Error fetching organization/workspace data:', error);

    // If the session points at an org that no longer exists (e.g. ID was
    // rewritten by a backfill), try to recover by re-fetching session and
    // selecting the user's first available workspace. Throws a redirect on
    // success; returns null on failure so the caller routes to onboarding.
    if (
      error?.data?.code === 'NOT_FOUND' ||
      error?.data?.code === 'FORBIDDEN'
    ) {
      await recoverFromStaleIds(targetPath);
    }

    return { organization: null, workspace: null, needsReload: false };
  }
}

export const Route = createFileRoute('/(protected)')({
  component: RouteComponent,
  beforeLoad: async ({ location }) => {
    // RFC-203: On a Trust Center custom domain, host-mode path(s) render the
    // PUBLIC Trust Center — `__root.tsx` renders `TrustCenterHostEntry` in place
    // of the `<Outlet/>`, so no protected component ever mounts. This guard runs
    // during route resolution, BEFORE the root renders; without this bail-out it
    // redirects the anonymous visitor to `/login` first. The public Trust Center
    // needs no session, so skip the gate. Predicate is shared with __root — see
    // `isTrustCenterHostModePath` for the invariant both sides must honor.
    if (isTrustCenterHostModePath(location.pathname)) {
      return
    }

    const targetPath = location.pathname;

    const session = await getSession()

    if (!session) {
      throw redirect({
        to:
          '/login?error=session-not-found&redirect_uri=' +
          encodeURIComponent(window.location.href),
      })
    }

    if (
      !session?.session?.expiresAt ||
      session.session.expiresAt < new Date()
    ) {
      throw redirect({
        to:
          '/login?error=session-expired&redirect_uri=' +
          encodeURIComponent(window.location.href),
      })
    }

    // Ensure organization and workspace data is available
    // This will auto-detect delegation needs from URL and set it if necessary
    const { organization, workspace, needsReload } = await ensureOrganizationAndWorkspaceData(session, targetPath);

    // If delegation was set or org was switched, reload to get fresh session
    if (needsReload) {
      // Clear session cache to force fresh fetch
      localStorage.removeItem('auth:session');

      // Reload the page
      window.location.reload();

      return { session, organization: null, workspace: null };
    }

    if (
      organization &&
      isOrgMfaEnforcementActive(organization) &&
      session.user?.twoFactorEnabled !== true
    ) {
      sessionStorage.setItem(MFA_SETUP_PENDING_REDIRECT_KEY, window.location.href);
      throw redirect({ to: '/setup-mfa' });
    }

    // If user has no organization, redirect to onboarding
    if (!session.session.activeOrganizationId || !organization) {
      // Check for partner delegation context first
      const delegationContext = localStorage.getItem('partner:delegation');
      const isDelegating = delegationContext ? JSON.parse(delegationContext).isDelegate : false;

      // If partner is delegating to a client, skip the organization check
      // The partner is not a member of the client org, but has delegated access
      if (!isDelegating) {
        // Check if user has any organizations at all
        try {
          const userOrgResult = await trpcClient.organizations.getUserOrganizations.query();
          const userOrgs = userOrgResult?.organizations

          if (!userOrgs || userOrgs.length === 0) {
            // User has no organizations, redirect to onboarding
            throw redirect({
              to: '/onboarding/quick-setup',
            })
          } else if (userOrgs.length > 0 && !session.session.activeOrganizationId) {
          // User has organizations but none is active, set the first one
          // This happens when an invited user logs in for the first time
          const firstOrg = userOrgs[0];

          // Set active org on the server session first to break the reload loop
          await trpcClient.organizations.setActiveOrganization.mutate({
            organizationId: firstOrg.id
          });

          localStorage.setItem('auth:activeOrg', JSON.stringify(firstOrg));

          // Get workspaces for this org
          const { organization: orgWithWorkspaces } = await trpcClient.organizations.getOrganizationWithWorkspaces.query({
            id: firstOrg.id
          });

          if (orgWithWorkspaces?.workspaces?.length > 0) {
            const firstWorkspace = orgWithWorkspaces.workspaces[0];
            localStorage.setItem('auth:activeWorkspace', JSON.stringify(firstWorkspace));
            localStorage.setItem('auth:activeWorkspaceId', firstWorkspace.id);
          }

          // Clear cached session so reload fetches fresh one with activeOrganizationId
          localStorage.removeItem('auth:session');

          // Reload to apply the changes
          window.location.reload();
          }
        } catch (error: any) {
          // Re-throw TanStack Router redirects (e.g. onboarding redirect) immediately
          if (isRedirect(error)) throw error;

          console.error('Error checking user organizations:', error);

          // If FORBIDDEN (403) or UNAUTHORIZED (401), user is not authenticated properly - redirect to login
          if (error?.data?.code === 'FORBIDDEN' || error?.data?.code === 'UNAUTHORIZED') {
            throw redirect({
              to: '/login?error=not-authorized&redirect_uri=' + encodeURIComponent(window.location.href),
            })
          }

          // For other errors, also redirect to login with a generic error
          // Don't redirect to quick-setup on error - only when we know user has no orgs
          throw redirect({
            to: '/login?error=failed-to-load-organizations&redirect_uri=' + encodeURIComponent(window.location.href),
          })
        }
      }
    }

    return {
      session,
      organization,
      workspace,
    }
  },
})

function RouteComponent() {
  const { setupAuthListener } = useAuthCheck()
  usePresenceHeartbeat()

  useEffect(() => {
    setupAuthListener()
  }, [setupAuthListener])

  return <Outlet />
}
